import { ArrowLeft } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { TrendChart } from '../components/TrendChart'
import {
  aggregateSpuDetailSkus,
  catalogSkusOf,
  sortDouyinRows,
  spuAllTimeSkuCount,
  spuDailyRowsAsc,
} from '../douyinCommerce'
import { DouyinStatisticsBar, douyinStatisticsSearch, douyinStatisticsWindow, useDouyinStatisticsPeriod } from '../douyinStatistics'
import { douyinPath, useDouyinStoreId } from '../douyinStore'
import type { DouyinLiveAnalysis } from '../douyinTypes'
import { integer, money } from '../utils'
import { DouyinBoundary, DouyinProductImage, DySigned, DySortTh, DyTag, dyPct1, dyWan } from './douyinCommon'

type TrendMetric = 'paid' | 'gm' | 'items'

const TREND_METRICS: Array<{ key: TrendMetric; label: string }> = [
  { key: 'paid', label: '支付金额' },
  { key: 'gm', label: '货差毛利' },
  { key: 'items', label: '成交件数' },
]

const DETAIL_SKU_COLUMNS: Array<{ key: string; label: string; left?: boolean; className?: string }> = [
  { key: 'n', label: '商品', left: true, className: 'dy-col-sku' },
  { key: 'paid', label: '支付金额' },
  { key: 'items', label: '成交件数' },
  { key: 'orders', label: '成交订单' },
  { key: 'uc', label: '单件成本' },
  { key: 'cost', label: '产品成本' },
  { key: 'gm', label: '货差毛利' },
  { key: 'gmr', label: '货差毛利率' },
  { key: 'refund', label: '退款金额' },
  { key: 'linked', label: '关联状态' },
]

export function DouyinSpuDetailPage() {
  const { productId = '' } = useParams<{ productId: string }>()
  return <DouyinBoundary>{(data) => <DouyinSpuDetailContent data={data} productId={productId} />}</DouyinBoundary>
}

function DouyinSpuDetailContent({ data, productId }: { data: DouyinLiveAnalysis; productId: string }) {
  const storeId = useDouyinStoreId()
  const [period, setPeriod] = useDouyinStatisticsPeriod('day')
  const window = douyinStatisticsWindow(data.meta, period)
  const statisticsSearch = douyinStatisticsSearch(period)
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('paid')
  const [dailyExpanded, setDailyExpanded] = useState(false)
  const [skuSort, setSkuSort] = useState({ key: 'paid', desc: true })

  const info = data.spuInfo[productId]
  const dailyAsc = useMemo(() => spuDailyRowsAsc(data, productId), [data, productId])
  const skuRows = useMemo(
    () => window ? aggregateSpuDetailSkus(data, productId, window) : [],
    [data, productId, window?.startDate, window?.endDate], // eslint-disable-line react-hooks/exhaustive-deps
  )
  if (!info) {
    return <div className="not-found"><h1>未找到该抖音SPU</h1><p>商品ID {productId || '未提供'}</p><Link to={`${douyinPath(storeId, '/spu')}${statisticsSearch}`}>返回SPU商品</Link></div>
  }
  const name = info.name || productId

  const aggregate = { paid: 0, items: 0, orders: 0, refund: 0, cost: 0, nocost: 0 }
  for (const row of dailyAsc) {
    if (!window || row.d < window.startDate || row.d > window.endDate) continue
    aggregate.paid += row.paid || 0
    aggregate.items += row.items || 0
    aggregate.orders += row.orders || 0
    aggregate.refund += row.refund || 0
    aggregate.cost += row.cost || 0
    aggregate.nocost += row.nocost || 0
  }
  const grossMargin = aggregate.paid - aggregate.cost
  const hasSales = dailyAsc.length > 0
  const allTimeSkuCount = spuAllTimeSkuCount(data, productId)
  const windowText = `${data.meta.queryStart} ~ ${data.meta.queryEnd}`

  const trendMoney = trendMetric !== 'items'
  const trendData = dailyAsc.map((row) => ({
    date: row.d,
    value: trendMetric === 'paid' ? row.paid || 0 : trendMetric === 'gm' ? (row.paid || 0) - (row.cost || 0) : row.items || 0,
  }))
  const daysDesc = dailyAsc.slice().reverse()
  const shownDays = dailyExpanded ? daysDesc : daysDesc.slice(0, 7)
  const catalogRows = skuRows.length ? [] : catalogSkusOf(data, productId)
  const toggleSkuSort = (key: string) => setSkuSort((current) => ({ key, desc: current.key === key ? !current.desc : true }))

  return <div className="page-stack dy-page dy-spu-detail-page">
    <div className="breadcrumb-row"><Link to={`${douyinPath(storeId, '/spu')}${statisticsSearch}`}><ArrowLeft aria-hidden="true" />返回</Link><span>SPU商品</span><span>/</span><span title={name}>{name}</span></div>
    <div className="pdd-spu-detail-identity">
      <div className="pdd-spu-detail-image"><DouyinProductImage src={info.img} alt={name} /></div>
      <div>
        <span className="dy-platform-label">DOUYIN SPU</span>
        <h1 title={name}>{name}</h1>
        <p>商品ID {productId} · 动销SKU {integer(allTimeSkuCount)}个 · 抖店SKU {info.variantCount != null ? integer(info.variantCount) : '—'}个</p>
      </div>
      <div className="pdd-spu-detail-status">
        {!hasSales
          ? <DyTag kind="off">窗口内无直播成交</DyTag>
          : aggregate.nocost > 0
            ? <DyTag kind="nocost">部分缺成本</DyTag>
            : <DyTag kind="coded">已接编码</DyTag>}
        <small>{info.inCatalog ? '在售商品库' : '已从商品库删除'}</small>
      </div>
    </div>
    <DouyinStatisticsBar period={period} onChange={setPeriod} window={window} title="统计表现范围" />
    <MetricStrip items={[
      { label: '支付金额', value: money(aggregate.paid) },
      { label: '货差毛利', value: money(grossMargin) },
      { label: '货差毛利率', value: dyPct1(aggregate.paid > 0 ? grossMargin / aggregate.paid : null) },
      { label: '成交件数', value: integer(aggregate.items) },
      { label: '成交订单', value: integer(aggregate.orders) },
      { label: '产品成本', value: money(aggregate.cost) },
      { label: '缺成本金额', value: money(aggregate.nocost) },
      { label: '退款金额', value: money(aggregate.refund) },
    ]} />
    <section className="overview-section">
      <div className="section-heading-row">
        <div><h2>每日趋势</h2><p>{hasSales ? `全部 ${integer(trendData.length)} 天 · 不随统计范围过滤` : '窗口内无直播成交'}</p></div>
        <div className="metric-switch" role="group" aria-label="趋势指标">
          {TREND_METRICS.map((item) => <button
            type="button"
            key={item.key}
            className={item.key === trendMetric ? 'is-active' : ''}
            aria-pressed={item.key === trendMetric}
            onClick={() => setTrendMetric(item.key)}
          >{item.label}</button>)}
        </div>
      </div>
      {hasSales
        ? <TrendChart
          data={trendData}
          valueFormatter={(value) => dyWan(value, trendMoney)}
          valueLabel={TREND_METRICS.find((item) => item.key === trendMetric)?.label}
          ariaLabel={`${name}每日${TREND_METRICS.find((item) => item.key === trendMetric)?.label}趋势`}
        />
        : <p className="dy-empty-hint">该商品在当前数据窗口（{windowText}）内无直播成交</p>}
    </section>
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>每日明细</h2><p>{hasSales ? `全部 ${integer(daysDesc.length)} 天 · 按日期降序` : '无成交日'}</p></div></div>
      {hasSales ? <>
        <div className="table-responsive"><table className="history-table dy-table"><thead><tr>
          <th className="l">日期</th><th>支付金额</th><th>货差毛利</th><th>货差毛利率</th><th>成交件数</th><th>成交订单</th><th>退款金额</th>
        </tr></thead><tbody>{shownDays.map((row) => {
          const gm = (row.paid || 0) - (row.cost || 0)
          return <tr key={row.d}>
            <td className="l">{row.d}</td>
            <td>{money(row.paid)}</td>
            <td><DySigned value={gm} /></td>
            <td>{dyPct1(row.paid > 0 ? gm / row.paid : null)}</td>
            <td>{integer(row.items)}</td>
            <td>{integer(row.orders)}</td>
            <td>{row.refund ? money(row.refund) : '—'}</td>
          </tr>
        })}</tbody></table></div>
        {daysDesc.length > 7 ? <button className="dy-expand-btn" type="button" onClick={() => setDailyExpanded((expanded) => !expanded)}>
          {dailyExpanded ? '收起' : `展开剩余 ${integer(daysDesc.length - 7)} 天`}
        </button> : null}
      </> : <p className="dy-empty-hint">该商品在当前数据窗口（{windowText}）内无直播成交</p>}
    </section>
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>旗下 SKU 销售明细</h2><p>{skuRows.length
        ? `${window?.label || ''} · 动销 ${integer(skuRows.length)} 个SKU`
        : catalogRows.length
          ? `商品库 ${integer(catalogRows.length)} 个SKU变体 · 当前范围内无直播成交`
          : '当前范围内无直播成交'}</p></div></div>
      {skuRows.length ? <div className="table-responsive"><table className="product-table dy-table dy-detail-sku-table"><thead><tr>
        {DETAIL_SKU_COLUMNS.map((column) => <DySortTh key={column.key} columnKey={column.key} label={column.label} left={column.left} className={column.className} sort={skuSort} onSort={toggleSkuSort} />)}
      </tr></thead><tbody>{sortDouyinRows(skuRows, skuSort, (row) => row.n).map((row) => <tr key={row.n}>
        <td className="l dy-col-sku">
          <div className="product-link">
            <DouyinProductImage src={row.img} />
            <span className="dy-prod-main"><strong title={row.n}>{row.n}</strong><small><span>编码 {row.code || '—'}</span></small></span>
          </div>
        </td>
        <td>{money(row.paid)}</td>
        <td>{integer(row.items)}</td>
        <td>{integer(row.orders)}</td>
        <td>{row.uc != null ? `${money(row.uc)}/件` : '—'}</td>
        <td>{row.cost != null ? money(row.cost) : '—'}</td>
        <td>{row.gm == null ? '—' : <DySigned value={row.gm} />}</td>
        <td>{dyPct1(row.gmr)}</td>
        <td>{row.refund ? money(row.refund) : '—'}</td>
        <td>{row.code ? <DyTag kind="coded">已接编码</DyTag> : <DyTag kind="uncoded">未接编码</DyTag>}</td>
      </tr>)}</tbody></table></div>
      : catalogRows.length ? <div className="table-responsive"><table className="product-table dy-table dy-detail-sku-table"><thead><tr>
        <th className="l dy-col-sku">商品</th><th>支付金额</th><th>成交件数</th><th>成交订单</th><th>单件成本</th><th>平台售价</th><th>抖店库存</th><th>抖店销量</th><th>关联状态</th>
      </tr></thead><tbody>{catalogRows.map((catalogSku) => <tr key={catalogSku.vid}>
        <td className="l dy-col-sku">
          <div className="product-link">
            <DouyinProductImage src={catalogSku.img} />
            <span className="dy-prod-main"><strong title={catalogSku.n}>{catalogSku.n}</strong><small><span>编码 {catalogSku.code || '—'}</span></small></span>
          </div>
        </td>
        <td><span className="dy-faint">¥0.00</span></td>
        <td><span className="dy-faint">0</span></td>
        <td><span className="dy-faint">0</span></td>
        <td>{catalogSku.uc != null ? `${money(catalogSku.uc)}/件` : '—'}</td>
        <td>{catalogSku.price != null ? money(catalogSku.price) : '—'}</td>
        <td>{catalogSku.stock != null ? integer(catalogSku.stock) : '—'}</td>
        <td className="dy-faint">{catalogSku.dyVol != null ? integer(catalogSku.dyVol) : '—'}</td>
        <td>{catalogSku.code ? <DyTag kind="coded">已接编码</DyTag> : <DyTag kind="uncoded">未接编码</DyTag>}</td>
      </tr>)}</tbody></table></div>
      : <p className="dy-empty-hint">该商品在当前数据窗口（{windowText}）内无直播成交，且已从商品库删除，无 SKU 档案可列。</p>}
    </section>
  </div>
}
